import React, { useEffect, useRef, useState } from "react";
import {
  Plus,
  Minus,
  ShoppingBag,
  Heart,
  Share2,
  ZoomIn,
  Loader2,
  Eye,
  EyeOff,
} from "lucide-react";
import { set } from "lodash";
import { NoBackpackSharp } from "@mui/icons-material";
import { connect } from "react-redux";
import PropTypes, { func } from "prop-types";
import { addToCart } from "../../redux/actions/cartActions";
import { addToWishlist } from "../../redux/actions/wishlistActions";
import { useToasts } from "react-toast-notifications";
import { Dialog, DialogContent, DialogTitle } from "@mui/material";

// 1. SPOSTATO FUORI (Definizione stabile)
// Funzione helper aggiornata per gestire query string (es. ?t=123)
const getFallbackExtension = (url) => {
  if (!url) return null;

  // LOGICA REGEX:
  // \.png       -> Cerca la stringa ".png"
  // (?=\?|$)    -> "Lookahead": verifica che dopo ci sia un "?" OPPURE la fine della stringa ($)
  // flag 'i'    -> Case insensitive (PNG, png, Png)

  // 1. Se è PNG, diventa JPG
  if (/\.png(?=\?|$)/i.test(url)) {
    return url.replace(/\.png(?=\?|$)/i, '.jpg');
  }

  // 2. Se è JPG/JPEG, diventa PNG
  if (/\.jpe?g(?=\?|$)/i.test(url)) {
    return url.replace(/\.jpe?g(?=\?|$)/i, '.png');
  }

  return null; // Nessuna estensione gestita trovata
};

const ImageWithLoader = ({
  src,
  alt,
  className,
  style,
  width,
  height,
  isSingleVariant,
  ...props
}) => {
  const [isLoading, setIsLoading] = useState(true);

  // Stato per gestire l'URL corrente (può cambiare se facciamo fallback)
  const [currentSrc, setCurrentSrc] = useState(src);

  // Stato per sapere se abbiamo già tentato il cambio estensione
  const [hasTriedFallback, setHasTriedFallback] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const imgRef = useRef(null);

  // --- LOGICA PINCH-TO-ZOOM E PANNING ---
  const [zoomScale, setZoomScale] = useState(1);
  const [zoomPos, setZoomPos] = useState({ x: 0, y: 0 });
  const [isZoomDragging, setIsZoomDragging] = useState(false);
  const touchState = useRef({
    distance: 0,
    startScale: 1,
    startX: 0,
    startY: 0,
    isPanning: false
  });

  useEffect(() => {
    if (!isModalOpen) {
      setZoomScale(1);
      setZoomPos({ x: 0, y: 0 });
    }
  }, [isModalOpen]);

  const onTouchStart = (e) => {
    setIsZoomDragging(true);
    if (e.touches.length === 2) {
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const dist = Math.hypot(touch1.clientX - touch2.clientX, touch1.clientY - touch2.clientY);
      touchState.current.distance = dist;
      touchState.current.startScale = zoomScale;
      touchState.current.isPanning = false;
    } else if (e.touches.length === 1 && zoomScale > 1) {
      touchState.current.startX = e.touches[0].clientX - zoomPos.x;
      touchState.current.startY = e.touches[0].clientY - zoomPos.y;
      touchState.current.isPanning = true;
    }
  };

  const onTouchMove = (e) => {
    if (e.touches.length === 2) {
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const dist = Math.hypot(touch1.clientX - touch2.clientX, touch1.clientY - touch2.clientY);
      if (touchState.current.distance > 0) {
        const newScale = touchState.current.startScale * (dist / touchState.current.distance);
        setZoomScale(Math.min(Math.max(1, newScale), 5)); // Zoom massimo: 5x
      }
    } else if (e.touches.length === 1 && touchState.current.isPanning && zoomScale > 1) {
      setZoomPos({
        x: e.touches[0].clientX - touchState.current.startX,
        y: e.touches[0].clientY - touchState.current.startY
      });
    }
  };

  const onTouchEnd = (e) => {
    if (e.touches.length < 2) {
      touchState.current.distance = 0;
    }
    if (e.touches.length === 0) {
      setIsZoomDragging(false);
      // Se lo zoom è inferiore a 1, ripristina la visuale standard
      if (zoomScale <= 1) {
        setZoomScale(1);
        setZoomPos({ x: 0, y: 0 });
      }
    }
  };

  const handleDoubleClick = () => {
    if (zoomScale > 1) {
      setZoomScale(1);
      setZoomPos({ x: 0, y: 0 });
    } else {
      setZoomScale(2.5); // Zoom rapido
      setZoomPos({ x: 0, y: 0 });
    }
  };

  // 1. RESET INIZIALE
  // Se la prop `src` cambia dall'esterno, resettiamo tutto
  useEffect(() => {
    setCurrentSrc(src);
    setHasTriedFallback(false);
    setIsLoading(true);
  }, [src]);

  // 2. CONTROLLO CACHE
  // Controlliamo se l'immagine (currentSrc) è già completa
  useEffect(() => {
    if (imgRef.current && imgRef.current.complete) {
      // Verifica opzionale: assicurarsi che naturalWidth sia > 0 per considerarla valida
      if (imgRef.current.naturalWidth > 0) {
        setIsLoading(false);
      }
    }
  }, [currentSrc]); // Esegui quando cambia currentSrc

  // 3. GESTORE ERRORI (Logica png <-> jpg)
  const handleError = () => {
    // Se abbiamo già provato il fallback e fallisce ancora, ci arrendiamo
    if (hasTriedFallback) {
      setIsLoading(false);
      return;
    }

    const fallbackUrl = getFallbackExtension(currentSrc);

    if (fallbackUrl) {
      // Proviamo la nuova estensione
      console.log(`Immagine non trovata: ${currentSrc}. Tento con: ${fallbackUrl}`);
      setHasTriedFallback(true);
      setCurrentSrc(fallbackUrl);
      // Non mettiamo isLoading(false) perché stiamo ancora aspettando la nuova immagine
    } else {
      // Non è un'estensione gestita, stop caricamento
      setIsLoading(false);
    }
  };


  return (
    <>
      <div
        className="position-relative d-flex align-items-center justify-content-center w-100"
        style={{ minHeight: height ? "auto" : "200px" }}
      >
        {/* LOADER SPINNER */}
        {isLoading && !isSingleVariant && (
          <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center z-1 bg-light rounded">
            <div
              className="spinner-border text-primary"
              role="status"
              style={{ width: "2rem", height: "2rem" }}
            >
              <span className="visually-hidden">Caricamento...</span>
            </div>
          </div>
        )}

        {/* IMMAGINE */}
        {!isSingleVariant && (
          <>
            <img
              ref={imgRef}
              src={currentSrc} // Usiamo lo stato interno, non la prop diretta
              alt={alt}
              width={width}
              height={height}
              style={{ ...style, transition: "opacity 0.3s ease-in-out" }}
              className={`${className || ""} ${isLoading ? "opacity-0" : "opacity-100"}`}
              // intrinsicsize="350x150" // Attenzione: intrinsicsize è deprecato/non standard in molti browser moderni
              onLoad={() => setIsLoading(false)}
              onError={handleError} // Qui scatta la magia del fallback
              {...props}
            />
            {!isLoading && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsModalOpen(true);
                }}
                style={{
                  position: "absolute",
                  top: "5px",
                  right: "5px",
                  background: "rgba(255, 255, 255, 0.85)",
                  border: "none",
                  borderRadius: "50%",
                  padding: "6px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 2,
                  boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
                }}
                title="Ingrandisci immagine"
              >
                <ZoomIn size={18} color="#333" />
              </button>
            )}
          </>
        )}
      </div>

      {/* POPUP FULLSCREEN */}
      <Dialog
        open={isModalOpen}
        onClose={(e) => {
          if (e) e.stopPropagation();
          setIsModalOpen(false);
        }}
        fullScreen
        PaperProps={{
          style: {
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            boxShadow: "none",
            overflow: "hidden",
          },
        }}
      >
        <div
          style={{
            position: "relative",
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            touchAction: "none", /* Impedisce allo schermo intero di muoversi mentre usi il pinch */
            cursor: zoomScale > 1 ? "grab" : "zoom-in"
          }}
          onClick={(e) => e.stopPropagation()}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onDoubleClick={handleDoubleClick}
        >
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsModalOpen(false);
            }}
            style={{
              position: "absolute",
              top: "20px",
              right: "20px",
              background: "rgba(255, 255, 255, 0.9)",
              border: "none",
              borderRadius: "50%",
              width: "36px",
              height: "36px",
              fontSize: "18px",
              fontWeight: "bold",
              cursor: "pointer",
              zIndex: 100,
              boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#333",
            }}
            title="Chiudi"
          >
            ✕
          </button>
          <img
            src={currentSrc}
            alt={alt}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              transform: `translate(${zoomPos.x}px, ${zoomPos.y}px) scale(${zoomScale})`,
              transformOrigin: "center",
              transition: isZoomDragging ? "none" : "transform 0.2s ease-out",
              willChange: "transform"
            }}
          />
        </div>
      </Dialog>
    </>
  );
};

const ProductSplitView = ({
  description,
  measure,
  imageModel,
  product,
  template,
  ord,
  pagina,
  variantOffset,
  offsetPerPage,
  listini,
  setEditDialog,
  editDialog,
  addToCart,
  cartItems,
}) => {
  // Stato iniziale
  const [variants, setVariants] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  const [variantsMeasure, setVariantsMeasure] = useState("");
  const [showVariants, setShowVariants] = useState(true);
  const [showDialogVarianti, setShowDialogVarianti] = useState(false);
  const [shownVariants, setShownVariants] = useState({});
  // Ref per tracciare le modifiche manuali e ignorare i ritardi di sincronizzazione con Redux
  const manualQtyOverrides = useRef({});
  const { addToast } = useToasts();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    console.log("Edit Dialog changed:", editDialog);
    var qty = parseInt(editDialog.currentQty);
    var qtaBox = parseInt(editDialog.pzBox);
    var prezzoBox = editDialog.preBox;
    if (qty > 0) {
      if (qty < editDialog.min) { qty = editDialog.min; }
      else if ((qty % editDialog.conf) !== 0) { qty = Math.ceil(qty / editDialog.conf) * editDialog.conf; }
    }
    if (template === "grid_with_sizes") {
      setVariants((prevVariants) =>
        prevVariants.map((color) => ({
          ...color,
          sizes: color.sizes.map((item) =>
            item.IDArtSizeColor === editDialog.id
              ? {
                ...item, quantity: qty,
                priceSco: qtaBox > 0 && qty >= qtaBox && parseFloat(prezzoBox) > 0 ? prezzoBox : undefined
              }
              : item
          ),
        }))
      );
    }
    else {
      setVariants((prevVariants) =>
        prevVariants.map((item) =>
          item.IDArtSizeColor === editDialog.id
            ? {
              ...item, quantity: qty,
              priceSco: qtaBox > 0 && qty >= qtaBox && parseFloat(prezzoBox) > 0 ? prezzoBox : undefined
            }
            : item
        )
      );
    }
  }, [editDialog]);

  // Funzione "Colabrodo" per fermare qualsiasi evento
  const stopAllEvents = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Ferma anche l'evento nativo nel caso la libreria ascolti il DOM direttamente
    if (e.nativeEvent) {
      e.nativeEvent.stopImmediatePropagation();
      e.nativeEvent.stopPropagation();
    }
  };

  function formattaTaglia(stringa) {
    // 1. Splittiamo: "3-4-3-4" -> ["3", "4", "3", "4"]  oppure  "3-3" -> ["3", "3"]
    const pezzi = stringa.split('-');

    // 2. Il Set rimuove i duplicati mantenendo l'ordine: ["3", "4"] oppure ["3"]
    const pezziUnici = [...new Set(pezzi)];

    // 3. Prendiamo al massimo i primi due elementi e li rimettiamo insieme
    return pezziUnici.slice(0, 2).join('-');
  }
  /**
   * Inverte la struttura di un catalogo prodotti da "Colore > Taglie" a "Taglia > Colori",
   * scambiando di conseguenza anche tutti i riferimenti interni di identificazione.
   * 
   * @param {Array} data - L'array originale strutturato per Colore.
   * @returns {Array} L'array invertito strutturato per Taglia.
   */
  function invertProductStructure(data) {
    if (!Array.isArray(data)) return [];

    const groupedBySize = data.reduce((acc, currentGroup) => {
      // Recuperiamo il colore principale del gruppo corrente (es. "S-S")
      const originalColor = currentGroup.color;

      // Cicliamo tra le varianti interne
      currentGroup.sizes.forEach((sizeItem) => {
        // Recuperiamo la taglia corrente (es. "_NY")
        const originalSize = sizeItem.sizeLabel;

        // Se la taglia non è ancora presente nell'accumulatore, creiamo il blocco principale
        if (!acc[originalSize]) {
          acc[originalSize] = {
            colorId: `c-${Object.keys(acc).length}`,
            color: originalSize.split("-")[0], // Il campo "color" ora ospita la taglia (es. "_NY")
            sizes: []
          };
        }

        // Generiamo il sotto-ID per la variante
        const newSubId = `${acc[originalSize].colorId}-${acc[originalSize].sizes.length}`;

        // Aggiungiamo i dati invertendo i campi richiesti
        acc[originalSize].sizes.push({
          id: newSubId,
          IDArtSizeColor: sizeItem.IDArtSizeColor,

          // Inversione dei campi di identificazione
          sizeLabel: formattaTaglia(originalColor),            // Diventa il colore (es. "S-S")
          selectedProductColor: formattaTaglia(originalSize),  // Diventa la taglia (es. "_NY")
          selectedProductSize: formattaTaglia(originalColor),  // Diventa il colore (es. "S-S")

          // Manteniamo inalterati tutti gli altri dati di business
          selectedIDArt: sizeItem.selectedIDArt,
          Ord1: sizeItem.Ord1,
          Ord2: sizeItem.Ord2,
          quantity: sizeItem.quantity,
          price: sizeItem.price,
          priceSco: sizeItem.priceSco,
          src: sizeItem.src,
          nomImg: sizeItem.nomImg,
          pzBox: sizeItem.pzBox,
          pzMin: sizeItem.pzMin,
          qtaConf: sizeItem.qtaConf,
          preBox: sizeItem.preBox,
          stock: sizeItem.stock
        });
      });

      return acc;
    }, {});

    // Trasformiamo l'oggetto di supporto in un array di JSON pronto all'uso
    return Object.values(groupedBySize);
  }
  useEffect(() => {
    let initialVariants = [];
    var m = "";
    // Controllo di sicurezza
    if (product && product.variation && product.variation.length > 0) {

      // Creazione lookup tables per ottimizzare le performance
      const cartItemsMap = new Map();
      if (cartItems && Array.isArray(cartItems)) {
        cartItems.forEach(item => {
          if (item.selectedIDArt) cartItemsMap.set(item.selectedIDArt, item.quantity);
        });
      }

      const listiniMap = new Map();
      if (listini && Array.isArray(listini)) {
        listini.forEach(l => {
          if (l.IDArt) listiniMap.set(l.IDArt, l);
        });
      }

      // --- LOGICA SPECIFICA PER GRID_WITH_SIZE (Struttura Nidificata) ---
      if (template === "grid_with_sizes") {

        var groupedVariants = product.variation.map((v, indexColor) => {

          var ret = {
            // Dati del Colore padre
            colorId: `c-${indexColor}`, // ID fittizio per il gruppo colore
            color: v.color || `Variante ${indexColor + 1}`,

            // Array delle taglie per questo colore
            sizes: (v.size && v.size.length > 0) ? v.size.map((sizeItem, indexSize) => {
              const listinoItem = listiniMap.get(sizeItem.IDArtSizeColor) || {};
              const cartQuantity = cartItemsMap.get(sizeItem.IDArtSizeColor) || 0;

              // Priorità all'input manuale recente per evitare il "rimbalzo a zero"
              let finalQuantity = cartQuantity;
              const manualOverride = manualQtyOverrides.current[sizeItem.IDArtSizeColor];
              if (manualOverride && (Date.now() - manualOverride.time < 2000)) {
                finalQuantity = manualOverride.qty;
              }

              const currentPzBox = listinoItem.pzBox || 1;
              const currentPreBox = listinoItem.preBox || "0.00";

              return {
                // ID univoco indispensabile per gestire le quantità
                id: `${indexColor}-${indexSize}`,
                IDArtSizeColor: sizeItem.IDArtSizeColor,
                // Info specifiche della taglia
                sizeLabel: sizeItem.name || sizeItem.label || "Unica",
                selectedProductColor: v.color,
                selectedProductSize: sizeItem.name || sizeItem.label || "Unica",
                selectedIDArt: sizeItem.IDArtSizeColor,
                Ord1: sizeItem.Ord1,
                Ord2: sizeItem.Ord2,
                quantity: finalQuantity, // Stato iniziale sincronizzato e protetto

                price: sizeItem["pricesco"] || sizeItem.pricesco || "0.00",
                priceSco: (currentPzBox > 0 && cartQuantity >= currentPzBox && parseFloat(currentPreBox) > 0) ? currentPreBox : undefined,
                src: global.baseUrlImage + sizeItem.nomImg, //+ "?t=" + Math.random(),
                nomImg: sizeItem.skuSizeColor,
                pzBox: currentPzBox,
                pzMin: listinoItem.pzMin || 1,
                qtaConf: listinoItem.qtaConf || 1,
                preBox: currentPreBox,
                stock: sizeItem.stock || 0,
              };
            }) : []
          };
          var articoloFinito = false;
          if (product.tag.includes("ARTICOLO FINITO")) {
            articoloFinito = true;
            console.log("Variante con tag ARTICOLO FINITO:", ret);
          }
          return ret;
        });

        if (product.tag.includes("ARTICOLO FINITO")) {
          groupedVariants = invertProductStructure(groupedVariants);
        }
        // Impostiamo lo stato con la struttura nidificata
        // Nota: Qui solitamente non si applica lo slice (paginazione) 
        // perché in una tabella si mostrano tutte le varianti disponibili.
        setVariants(groupedVariants.slice(variantOffset, variantOffset + offsetPerPage));
        //console.log("Grouped Variants:", groupedVariants);
      }
      // --- LOGICA STANDARD (Struttura Piatta / Flat) ---
      else {
        // Usiamo flatMap per ottenere un unico array piatto
        initialVariants = product.variation.flatMap((v, indexColor) => {
          if (!v || !v.size || v.size.length === 0) return [];
          return v.size.map((sizeItem, indexSize) => {

            if (template === "photo_variant" && sizeItem.IDArtSizeColor != pagina.IDArtVariante) return null;
            m = (m || "") + " " + sizeItem.measure + ", ";

            const listinoItem = listiniMap.get(sizeItem.IDArtSizeColor) || {};
            const cartQuantity = cartItemsMap.get(sizeItem.IDArtSizeColor) || 0;
            const currentId = `${indexColor}-${indexSize}`;

            let finalQuantity = cartQuantity;
            const manualOverride = manualQtyOverrides.current[currentId];
            if (manualOverride && (Date.now() - manualOverride.time < 2000)) {
              finalQuantity = manualOverride.qty;
            }

            const currentPzBox = listinoItem.pzBox || 1;
            const currentPreBox = listinoItem.preBox || "0.00";

            return {
              id: currentId,
              IDArtSizeColor: sizeItem.IDArtSizeColor,
              color: v.colore || `Variante ${indexColor + 1}`,
              selectedProductColor: v.color,
              selectedProductSize: sizeItem.name || sizeItem.label || "Unica",
              selectedIDArt: sizeItem.IDArtSizeColor,
              Ord1: sizeItem.Ord1,
              Ord2: sizeItem.Ord2,
              // sizeLabel opzionale nel flat view, ma utile averlo
              sizeLabel: sizeItem.taglia || sizeItem.label,
              quantity: finalQuantity,
              price: sizeItem["pricesco"] || sizeItem.pricesco || "0.00",
              priceSco: (currentPzBox > 0 && cartQuantity >= currentPzBox && parseFloat(currentPreBox) > 0) ? currentPreBox : undefined,
              src: global.baseUrlImage + sizeItem.nomImg,//+ "?t=" + Math.random(),
              nomImg: sizeItem.skuSizeColor,
              pzBox: currentPzBox,
              pzMin: listinoItem.pzMin || 1,
              qtaConf: listinoItem.qtaConf || 1,
              preBox: currentPreBox,
              stock: sizeItem.stock || 0,
            };
          });

        });
        m = m.slice(0, -2); // Rimuovi l'ultima virgola e spazio
        var measures = m.split(", ").slice(variantOffset, variantOffset + offsetPerPage).join(" // ");
        measures = measures.replaceAll("undefined // ", "");
        measures = measures.replaceAll("undefined", "");
        setVariantsMeasure(measures);
        if (template === "photo_variant") {
          setVariants(
            initialVariants.filter(v => v != null)
          );
        } else {
          setVariants(
            initialVariants.filter(v => v != null).slice(variantOffset, variantOffset + offsetPerPage)
          );
        }
      }
    }
  }, [product.variation, template, variantOffset, offsetPerPage, cartItems]); // Aggiunto 'template' alle dipendenze

  const addVariant = () => {
    const newId = variants.length + 1;
    const isEven = newId % 2 === 0;
    setVariants([
      ...variants,
      {
        id: newId,
        color: isEven ? "beige" : "blue",
        quantity: 0,
        price: "49.00", // Default price per nuove varianti
        src: isEven
          ? "https://images.unsplash.com/photo-1591561954557-26941169b49e?auto=format&fit=crop&q=80&w=800"
          : "https://images.unsplash.com/photo-1575296093822-980ce4b1b0e7?auto=format&fit=crop&q=80&w=800",
      },
    ]);
  };

  const removeVariant = () => {
    if (variants.length > 0) {
      setVariants(variants.slice(0, -1));
    }
  };

  const updateQuantity = async (e, id, delta, qtaMin, qtaConf, qtaBox, prezzoBox) => {
    if (e) e.stopPropagation(); // <--- AGGIUNGI QUESTA RIGA
    var minQta = qtaMin || 1;
    delta = delta * (qtaConf || 1);
    if (delta < 0) {
      minQta = 0;
    }
    if (template === "grid_with_sizes") {
      setVariants((prevVariants) =>
        prevVariants.map((color) => ({
          ...color,
          sizes: color.sizes.map((item) =>
            item.IDArtSizeColor === id
              ? {
                ...item, quantity: Math.min(item.stock, Math.max(minQta, item.quantity === qtaMin && delta < 0 ? 0 : item.quantity + delta)),
                priceSco: qtaBox > 0 && Math.max(minQta, item.quantity === qtaMin && delta < 0 ? 0 : item.quantity + delta) >= qtaBox && parseFloat(prezzoBox) > 0 ? prezzoBox : undefined
              }
              : item
          ),
        }))
      );
      if (shownVariants.sizes) {
        setShownVariants((prevVariants) =>
        ({
          ...prevVariants,
          sizes: prevVariants.sizes.map((item) =>
            item.IDArtSizeColor === id
              ? {
                ...item, quantity: Math.min(item.stock, Math.max(minQta, item.quantity === qtaMin && delta < 0 ? 0 : item.quantity + delta)),
                priceSco: qtaBox > 0 && Math.max(minQta, item.quantity === qtaMin && delta < 0 ? 0 : item.quantity + delta) >= qtaBox && parseFloat(prezzoBox) > 0 ? prezzoBox : undefined
              }
              : item
          ),
        })
        );
      }
    }
    else {
      setVariants((prevVariants) =>
        prevVariants.map((item) =>
          item.id === id
            ? {
              ...item, quantity: Math.min(item.stock, Math.max(minQta, item.quantity === qtaMin && delta < 0 ? 0 : item.quantity + delta)),
              priceSco: qtaBox > 0 && Math.max(minQta, item.quantity === qtaMin && delta < 0 ? 0 : item.quantity + delta) >= qtaBox && parseFloat(prezzoBox) > 0 ? prezzoBox : undefined
            }
            : item
        )
      );
    }
    let variant = null;
    if (template === "grid_with_sizes") {
      variant = variants.flatMap(v => v.sizes).find(s => s.IDArtSizeColor === id);
    }
    else variant = variants.find(v => v.id === id);
    if (variant.stock <= 0) {
      addToast("Prodotto esaurito", { appearance: "error", autoDismiss: true });
      return;
    }
    if (variant.quantity < variant.stock && variant.quantity >= 0) addToCart(
      product,
      null,
      variant.quantity >= variant.stock ? 0 : Math.min(variant.stock, Math.max(minQta, variant.quantity === qtaMin && delta < 0 ? 0 : variant.quantity + delta)) - variant.quantity,
      variant.selectedProductColor,
      variant.selectedProductSize,
      variant.selectedIDArt,
      variant.Ord1,
      variant.Ord2
    );
    if (delta == 0) addToCart(
      product,
      null,
      -variant.quantity,
      variant.selectedProductColor,
      variant.selectedProductSize,
      variant.selectedIDArt,
      variant.Ord1,
      variant.Ord2
    );
  };

  const handleInputChange = (e, id, value) => {
    if (e) e.stopPropagation(); // <--- AGGIUNGI QUESTA RIGA
    const qty = parseInt(value);
    const finalQty = isNaN(qty) ? 0 : Math.max(0, qty);
    manualQtyOverrides.current[id] = { qty: finalQty, time: Date.now() };

    if (template === "grid_with_sizes") {
      setVariants((prevVariants) =>
        prevVariants.map((color) => ({
          ...color,
          sizes: color.sizes.map((item) =>
            item.IDArtSizeColor === id ? { ...item, quantity: finalQty } : item
          ),
        }))
      );
      if (shownVariants && shownVariants.sizes) {
        setShownVariants((prev) => ({
          ...prev,
          sizes: prev.sizes.map((s) => (s.IDArtSizeColor === id ? { ...s, quantity: finalQty } : s)),
        }));
      }
    } else {
      setVariants((prevVariants) =>
        prevVariants.map((item) => (item.id === id ? { ...item, quantity: finalQty } : item))
      );
    }
  };

  const getGridClass = () => {
    const count = variants.length;
    if (template === "grid") {
      if (count <= 4) return "grid-quad";
      else return "grid-many";
    }
    if (template === "grid_with_sizes") {
      return "grid-quad";
    }
    if (count <= 1) return "grid-single";
    if (count === 2) return "grid-double";
    if (count <= 4) return "grid-quad";
    return "grid-many";
  };

  // Funzione helper per formatto del prezzo
  const formatPrice = (price) => {
    return Number(price).toLocaleString("it-IT", {
      style: "currency",
      currency: "EUR",
    });
  };
  const trovaPrezzoScontato = (v) => {
    if (v && v.sizes && v.sizes.length > 0) {
      for (let j = 0; j < v.sizes.length; j++) {
        if (v.sizes[j].priceSco && parseFloat(v.sizes[j].priceSco) > 0) {
          return v.sizes[j].priceSco;
        }
      }
    }
    return undefined;
  }
  const trovaPrezzo = (v) => {
    if (v && v.sizes && v.sizes.length > 0) {
      for (let j = 0; j < v.sizes.length; j++) {
        if (v.sizes[j].price && parseFloat(v.sizes[j].price) > 0) {
          return v.sizes[j].price;
        }
      }
    }
    return undefined;
  }
  return (
    <>
      <style>{`
        /* Reset e Base */
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body, html { height: 100%; background: #fff; }

        .app-container {
          display: flex;
          flex-direction: column;
          min-height: 75vh;
          height: 75vh !important;
          max-width: 1200px;
          min-width:100%;
          margin: 0 auto;
          background-color: white;
        }

        /* --- HEADER --- */
        .header {
          background-color: rgba(173, 208, 226, 1); 
          padding: 1.5rem 1rem;
          text-align: center;
          min-height: 10vh;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .header-title {
          font-family: "Montserrat", "Times New Roman", serif;
          font-size: 2rem;
          color: #000;
          letter-spacing: 1px;
        }

        /* --- MAIN LAYOUT --- */
        .main-content {
          display: flex;
          flex-direction: row;
          flex-grow: 1;
          min-height: 65vh;
        }

        @media (min-width: 768px) {
          .main-content {
            flex-direction: row;
            align-items: stretch;
          }
        }

        /* --- COLONNA SINISTRA --- */
        .left-column {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0rem;
        }

        .model-img {
          height: 600px;
          object-fit: contain;
        }

        /* --- COLONNA DESTRA --- */
        .right-column {
          flex: 1;
          display: flex;
          flex-direction: column;
          padding: 0.5rem;
          max-height: 65vh;
          overflow-y: hidden; 
          justify-content: center;
        }

        /* --- MODIFICHE FLEXBOX PER LA GRIGLIA --- */
        .variants-grid {
          display: flex;  
          flex-direction: row;        
          flex-wrap: wrap;        
          justify-content: center;
          gap: 0.3rem;              
          width: 100%;
          align-content: center;  
          padding: 1rem;
          max-height: 65vh;
          overflow-y: hidden;
        }
        .variants-grid-one-column {
          display: grid;  
          grid-template-columns: 1fr;
          flex-direction: row;        
          flex-wrap: wrap;        
          justify-content: center;
          gap: 0.3rem;              
          width: 100%;
          align-content: center;  
          padding: 0.3rem;
          max-height: 65vh;
          overflow-y: hidden;
        }
        /* ITEM PRODOTTO */
        .product-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }
        .product-item-with-sizes{
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: top;
        }
        /* 1 colonna (100% width) */
        .grid-single .product-item, .grid-single .product-item-with-sizes { 
          width: 100%; 
        }

        .grid-double .product-item,
        .grid-double .product-item-with-sizes{
          width: calc(100% - 0.7rem); 
        }

        /* 2 colonne (calc 50% - gap) */
        .grid-quad .product-item,
        .grid-quad .product-item-with-sizes { 
          width: calc(50% - 0.7rem); 
        }
        .grid-double {
          flex-direction: column;
        }
        /* 3 colonne (calc 33.33% - gap) */
        .grid-many .product-item, .grid-many .product-item-with-sizes{ 
          width: calc(33.333% - 1.34rem); 
        }

        .product-img {
          width: 100%;
          height: auto;
          min-height: 200px;
          max-height: 200px;
          object-fit: contain;
          margin-bottom: 5px; 
        }

        /* --- STILE PREZZO --- */
        .product-price {
            font-family: "Montserrat", sans-serif;
            font-size: 1.1rem;
            font-weight: 500;
            color: #333;
            margin-bottom: 10px;
            text-align: center;
        }

        /* --- CONTROLLI QUANTITÀ --- */
        .qty-controls {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0;
          border: 1px solid #ddd;
          border-radius: 4px;
          overflow: hidden;
          background: #fff;
          width: 100px;
          height: 40px;
        }
        .qty-controls-min {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0;
          border: 1px solid #ddd;
          border-radius: 4px;
          overflow: hidden;
          background: #fff;
          width: 80px;
          height: 35px;
        }

        .qty-btn {
          background: #f9f9f9;
          border: none;
          width: 30px;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.2s;
          color: #555;
        }

        .qty-btn:hover {
          background: #e0e0e0;
        }

        .qty-btn:active {
          background: #ccc;
        }

        .qty-input {
          width: 35px;
          height: 100%;
          border: none;
          border-left: 1px solid #ddd;
          border-right: 1px solid #ddd;
          text-align: center;
          font-size: 1.2rem;
          color: #333;
          -moz-appearance: textfield; 
        }
        .qty-input-min {
          width: 35px;
          height: 100%;
          border: none;
          border-left: 1px solid #ddd;
          border-right: 1px solid #ddd;
          text-align: center;
          font-size: 1rem;
          color: #333;
          -moz-appearance: textfield; 
        }
        
        .qty-input::-webkit-outer-spin-button,
        .qty-input::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }

        .qty-input:focus {
          outline: none;
          background: #fefefe;
        }

        /* --- FOOTER --- */
        .footer {
          background-color: #FDF5A5; 
          padding: 0.5rem;
          text-align: center;
        }

        .footer-text {
          font-weight: 300;
          font-size: 0.9rem;
          color: #000;
          font-family: Montserrat, sans-serif;
        }

        /* --- DEBUG CONTROLS --- */
        .debug-controls {
          background: #eee;
          padding: 10px;
          text-align: center;
          font-size: 0.8rem;
          color: #666;
          border-top: 1px solid #ddd;
        }
        .btn { margin: 0 5px; padding: 5px 10px; cursor: pointer; }

        @media (min-width: 768px) {
            .header-title { font-size: 2.5rem; }
        }

        /* ------------------------------------------------------------- */
        /* --- CONFIGURAZIONE RESPONSIVE MOBILE RIGIDA SENZA BUCHI --- */
        /* ------------------------------------------------------------- */
        @media (max-width: 900px) {

        .footer-text{
        line-height: 13px !important;
        margin-bottom: 1px !important;
        }
          .product-img {
            width: 100%;
            height: auto;
            min-height: 90px;
            max-height: 90px;
            object-fit: contain;
            margin-bottom: 5px;
          }
            .product-img-solo {
            width: 100%;
            height: auto;
            min-height: 100px;
            max-height: 100px;
            object-fit: contain;
            margin-bottom: 5px;
          }
          .product-img-solo-dialog {
            width: 100%;
            height: auto;
            min-height: 200px;
            max-height: 200px;
            object-fit: contain;
            margin-bottom: 5px;
          }
          /* Forza l'intera app a occupare esattamente l'altezza disponibile dello schermo (100dvh) */
          .app-container {
            height: 100vh !important;
            height: 80dvh !important; /* Supporto browser moderni per escludere le barre del browser */
            min-height: 100vh !important;
            min-height: 80dvh !important;
            margin-left: 0 !important;
            display: flex;
            flex-direction: column;
            overflow: hidden; /* Blocca lo scroll globale della pagina esterna */
          }
          
          /* Altezze calibrate per i tre blocchi principali per un totale di 100% dello schermo */
          .header {
            height: 10vh !important;
            min-height: 10vh !important;
            padding: 0px 10px;
            width: 100%;
            margin-left: 0;
          }
          
          /* Il corpo centrale prende lo spazio rimanente sottratto da header e footer */
          .main-content {
            position: relative; 
            flex-direction: column;
            height: 60vh !important; /* Altezza fissa calibrata sul viewport */
            min-height: 60vh !important;
            background-size: cover !important; /* Cambiato a 'cover' per riempire l'intero blocco senza buchi */
            background-position: center center !important;
            max-height: none !important;
            overflow: hidden;
          }
          
          /* Colonna di sinistra (sfondo indossato) clona l'altezza esatta del contenitore */
          .left-column {
            flex: none !important;
            width: 100%;
            height: 100% !important;
            min-height: 100% !important;
          }
          
          /* Sovrapposizione assoluta perfetta: prende il 100% dell'altezza del main-content */
          .right-column {
            position: absolute !important;
            top: 0;
            left: 0;
            width: 100% !important;
            height: 100% !important; /* Copre al millimetro il main-content */
            min-height: 100% !important;
            z-index: 2; 
            overflow-y: auto !important; /* Abilita lo scroll ESCLUSIVAMENTE dentro la griglia varianti */
            padding: 1rem 0.5rem;
            -webkit-overflow-scrolling: touch !important; /* Abilita lo scroll inerziale (fluido) nativo su iOS */
          }
          
          /* Il footer occupa l'ultimo slot rimasto a schermo */
          .footer {
            height: 8vh !important;
            min-height: 8vh !important;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            padding: 0.3rem;
          }
          
          .variants-grid, .variants-grid-one-column {
            max-height: none !important;
            overflow-y: visible !important; 
            padding: 0.5rem;
            width: 100%; 
            margin: 0 auto;
          }
          
          .grid-many .product-item, .grid-many .product-item-with-sizes,
          .grid-quad .product-item, .grid-quad .product-item-with-sizes {
            width: calc(50% - 0.7rem); 
          }
          
          .header-title {
            display: block !important;
            white-space: normal !important;
            line-height: 1.3 !important;
            font-size: 1.1rem !important;
            word-wrap: break-word !important;
          }
        }
      `}</style>

      <div className="app-container" style={{ margin: "0 auto", width: "100%" }}>
        <header className="header">
          <p
            className="header-title"
            style={{
              fontFamily: "Poiret One, Times New Roman, serif",
              fontWeight: "600",
              fontSize: "1.2rem",
              justifyContent: "center",
              display: "flex",
              alignItems: "center",
            }}
          >
            {pagina.showDescription ? description : null}
          </p>
        </header>

        {product && product.brands && product.brands.length > 0 ? (
          <div style={{ alignSelf: "center", textAlign: "center", padding: "10px", marginTop: "6rem", position: "absolute", backgroundColor: "white", zIndex: 3, borderRadius: "8px", boxShadow: "0 0 10px rgba(0,0,0,0.1)" }}>
            <p style={{ color: "red", fontWeight: "500" }}>{product.brands}</p>
          </div>
        ) : null}

        <div
          className="main-content"
          style={{
            backgroundImage: template === "mixed" || template === "photo_variant" || template === "single_variant" ? `url(${imageModel})` : "none",
            backgroundRepeat: "no-repeat"
          }}
        >
          {(template === "mixed" ||
            template === "single_variant" || template === "photo_variant" ||
            template === "full_photo") && (
              <div
                className="left-column"
                style={
                  template === "single_variant" || template === "photo_variant" ? { flex: "3" } : { flex: "1" }
                }
              >
              </div>
            )}

          {template !== "full_photo" && template !== "grid_with_sizes" && (
            <div
              className="right-column"
              style={{
                display: (!isMobile || showVariants) ? "flex" : "none",
                backgroundColor: isMobile ? "rgba(255, 255, 255, 0.85)" : "transparent"
              }}
            >
              {variants.length === 0 ? (
                <p style={{ textAlign: "center", color: "#ccc" }}>
                  Nessuna variante
                </p>
              ) : (
                <div className={variants.length <= 2 && template !== 'grid' ? `variants-grid-one-column ${getGridClass()}` : `variants-grid ${getGridClass()}`}>
                  {variants.map((v) => (
                    <div key={v.id} className="product-item">
                      {/* Immagine Prodotto */}
                      <ImageWithLoader
                        src={v.src}
                        alt={v.nomImg}
                        width={700}
                        height={500}
                        style={{
                          aspectRatio: "auto attr(width) / attr(height)",
                        }}
                        className="product-img img-fluid max-w-full h-auto mx-auto"
                        isSingleVariant={template === "single_variant" || template === "photo_variant"}
                      />

                      {/* --- PREZZO --- */}
                      <div className="product-price">
                        {v.priceSco ? <small><s>{formatPrice(v.price)}</s></small> : null}
                        {v.priceSco ? <b>{formatPrice(v.priceSco)}</b> : formatPrice(v.price)}
                      </div>

                      {/* Controlli Quantità */}
                      <div className="qty-controls"
                        onPointerDown={stopAllEvents}
                        onTouchStart={stopAllEvents}
                        style={{ cursor: 'auto' }}
                      >
                        <button
                          className="qty-btn"
                          onClick={(e) => {
                            stopAllEvents(e);
                            updateQuantity(e, v.id, -1, v.pzMin, v.qtaConf, v.pzBox, v.preBox);
                          }}
                          onMouseMove={(e) => stopAllEvents(e)}
                          aria-label="Diminuisci quantità"
                        >
                          <Minus size={20} />
                        </button>

                        <input
                          type="number"
                          className="qty-input"
                          onMouseDown={(e) => e.stopPropagation()}
                          onTouchStart={(e) => e.stopPropagation()}
                          value={v.quantity}
                          onChange={(e) => handleInputChange(e, v.id, e.target.value)}
                          min="0"
                          onDoubleClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setEditDialog({
                              isOpen: true,
                              id: v.id,
                              currentQty: v.quantity,
                              min: v.pzMin || 0,
                              conf: v.qtaConf || 1,
                              pzBox: v.pzBox || 1,
                              preBox: v.preBox || "0.00",
                              updateQuantity,
                              prevQty: v.quantity
                            });
                          }}
                        />

                        <button
                          className="qty-btn"
                          onClick={(e) => {
                            stopAllEvents(e);
                            updateQuantity(e, v.id, 1, v.pzMin, v.qtaConf, v.pzBox, v.preBox);
                          }}
                          onMouseMove={(e) => stopAllEvents(e)}
                          aria-label="Aumenta quantità"
                        >
                          <Plus size={20} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {shownVariants && (<Dialog open={showDialogVarianti} onClose={() => setShowDialogVarianti(false)}>
            <DialogTitle id="alert-dialog-title">
              {"Dettaglio Variante " + shownVariants.color}
            </DialogTitle>
            <DialogContent>
              {shownVariants?.sizes && shownVariants?.sizes.length > 0 && (
                <ImageWithLoader
                  src={shownVariants?.sizes[0].src}
                  width={isMobile ? 600 : 600}
                  height={isMobile ? 600 : 300}
                  style={{
                    aspectRatio: "auto attr(width) / attr(height)",
                  }}
                  className={isMobile ? "product-img-solo-dialog img-fluid max-w-full mx-auto" : "product-img img-fluid max-w-full mx-auto "}

                />)}
              <table style={{ margin: "auto", marginTop: "10px", marginBottom: "10px", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ fontSize: "12px", width: "15vw" }}>Taglia</th>
                    <th style={{ fontSize: "12px" }}>Qta</th>
                  </tr>
                </thead>
                <tbody>
                  {shownVariants?.sizes?.map((sizeItem) => (
                    <tr key={sizeItem.id}>
                      <td style={{ textAlign: "center", padding: "0px 10px", containerType: "inline-size" }}>
                        <span style={{ fontSize: "12px" }}>
                          {sizeItem.sizeLabel}
                          {sizeItem.priceSco ? "*" : <>&nbsp;</>}
                        </span>
                      </td>
                      <td>
                        <div className="qty-controls-min"
                          onClick={stopAllEvents}>
                          <button
                            className="qty-btn" onPointerDown={stopAllEvents}
                            onTouchStart={stopAllEvents} onClick={(e) => {
                              var qtaMin = sizeItem.pzMin || 1;
                              var qtaConf = sizeItem.qtaConf || 1;
                              updateQuantity(e, sizeItem.IDArtSizeColor, -1, qtaMin, qtaConf, sizeItem.pzBox, sizeItem.preBox);
                            }}
                            onMouseMove={(e) => stopAllEvents(e)}
                            aria-label="Diminuisci quantità"
                          >
                            <Minus size={18} />
                          </button>

                          <input
                            type="number"
                            className="qty-input-min"
                            onMouseDown={(e) => e.stopPropagation()}
                            onTouchStart={(e) => e.stopPropagation()}
                            value={sizeItem.quantity}
                            onChange={(e) =>
                              handleInputChange(e, sizeItem.IDArtSizeColor, e.target.value)
                            }
                            min="0"
                            onDoubleClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              setEditDialog({
                                isOpen: true,
                                id: sizeItem.IDArtSizeColor,
                                currentQty: sizeItem.quantity,
                                min: sizeItem.pzMin || 0,
                                conf: sizeItem.qtaConf || 1,
                                pzBox: sizeItem.pzBox || 1,
                                preBox: sizeItem.preBox || "0.00",
                                updateQuantity,
                                prevQty: sizeItem.quantity
                              });
                            }}
                          />

                          <button
                            className="qty-btn" onPointerDown={stopAllEvents}
                            onTouchStart={stopAllEvents} onClick={(e) => { stopAllEvents(e); updateQuantity(e, sizeItem.IDArtSizeColor, 1, sizeItem.pzMin, sizeItem.qtaConf, sizeItem.pzBox, sizeItem.preBox); }}
                            aria-label="Aumenta quantità"
                            onMouseMove={(e) => stopAllEvents(e)}
                          >
                            <Plus size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="product-price">
                {trovaPrezzoScontato(shownVariants) ? <small>{formatPrice(trovaPrezzo(shownVariants))}</small> : formatPrice(trovaPrezzo(shownVariants))}
                {trovaPrezzoScontato(shownVariants) && <b>&nbsp;*{formatPrice(trovaPrezzoScontato(shownVariants))}</b>}
              </div>
            </DialogContent>
          </Dialog>)}
          {template === "grid_with_sizes" && (
            <div
              className="right-column"
              style={{
                display: (!isMobile || showVariants) ? "flex" : "none",
                backgroundColor: isMobile ? "rgba(255, 255, 255, 0.85)" : "transparent"
              }}
            >
              {variants.length === 0 ? (
                <p style={{ textAlign: "center", color: "#ccc" }}>
                  Nessuna variante
                </p>
              ) : (
                <div className={`variants-grid ${getGridClass()}`}>
                  {variants.map((v) => (
                    <div key={v.id} className="product-item-with-sizes">
                      {/* Immagine Prodotto */}
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: isMobile ? '100%' : 'auto' }}>
                        <ImageWithLoader
                          src={v.sizes[0].src}
                          width={isMobile ? 250 : 600}
                          height={isMobile ? 450 : 300}
                          style={{
                            aspectRatio: "auto attr(width) / attr(height)",
                          }}
                          className={isMobile ? "product-img-solo img-fluid max-w-full mx-auto" : "product-img img-fluid max-w-full mx-auto "}
                          isSingleVariant={template === "single_variant" || template === "photo_variant"}
                        />
                        {isMobile && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowDialogVarianti(true);
                              setShownVariants(v);
                            }}
                            style={{
                              marginTop: "2px",
                              marginBottom: "2px",
                              padding: "4px 6px",
                              backgroundColor: "#2e98f0",
                              color: "white",
                              border: "none",
                              borderRadius: "6px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "5px",
                              fontWeight: "600",
                              fontSize: "8px",
                              boxShadow: "0 2px 5px rgba(0,0,0,0.15)",
                              width: "95%",
                              maxWidth: "180px",
                              cursor: "pointer"
                            }}
                          >
                            <ShoppingBag size={16} />
                            Aggiungi al carrello
                          </button>
                        )}
                      </div>
                      {!isMobile && <div>
                        <span
                          style={{
                            fontSize: "12px",
                            display: "block",
                            maxWidth: "100%",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis"
                          }}
                          title={v.color}
                        >
                          {v.color}
                        </span>
                        <table>
                          <thead>
                            <tr>
                              <th style={{ fontSize: "12px", width: "15vw" }}>Taglia</th>
                              <th style={{ fontSize: "12px" }}>Qta</th>
                            </tr>
                          </thead>
                          <tbody>
                            {v.sizes.map((sizeItem) => (
                              <tr key={sizeItem.id}>
                                <td style={{ textAlign: "center", padding: "0px 10px", containerType: "inline-size" }}>
                                  <span style={{ fontSize: "12px" }}>
                                    {sizeItem.sizeLabel}
                                    {sizeItem.priceSco ? "*" : <>&nbsp;</>}
                                  </span>
                                </td>
                                <td>
                                  <div className="qty-controls-min"
                                    onClick={stopAllEvents}>
                                    <button
                                      className="qty-btn" onPointerDown={stopAllEvents}
                                      onTouchStart={stopAllEvents} onClick={(e) => {
                                        var qtaMin = sizeItem.pzMin || 1;
                                        var qtaConf = sizeItem.qtaConf || 1;
                                        updateQuantity(e, sizeItem.IDArtSizeColor, -1, qtaMin, qtaConf, sizeItem.pzBox, sizeItem.preBox);
                                      }}
                                      onMouseMove={(e) => stopAllEvents(e)}
                                      aria-label="Diminuisci quantità"
                                    >
                                      <Minus size={18} />
                                    </button>

                                    <input
                                      type="number"
                                      className="qty-input-min"
                                      onMouseDown={(e) => e.stopPropagation()}
                                      onTouchStart={(e) => e.stopPropagation()}
                                      value={sizeItem.quantity}
                                      onChange={(e) =>
                                        handleInputChange(e, sizeItem.IDArtSizeColor, e.target.value)
                                      }
                                      min="0"
                                      onDoubleClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        setEditDialog({
                                          isOpen: true,
                                          id: sizeItem.IDArtSizeColor,
                                          currentQty: sizeItem.quantity,
                                          min: sizeItem.pzMin || 0,
                                          conf: sizeItem.qtaConf || 1,
                                          pzBox: sizeItem.pzBox || 1,
                                          preBox: sizeItem.preBox || "0.00",
                                          updateQuantity,
                                          prevQty: sizeItem.quantity
                                        });
                                      }}
                                    />

                                    <button
                                      className="qty-btn" onPointerDown={stopAllEvents}
                                      onTouchStart={stopAllEvents} onClick={(e) => { stopAllEvents(e); updateQuantity(e, sizeItem.IDArtSizeColor, 1, sizeItem.pzMin, sizeItem.qtaConf, sizeItem.pzBox, sizeItem.preBox); }}
                                      aria-label="Aumenta quantità"
                                      onMouseMove={(e) => stopAllEvents(e)}
                                    >
                                      <Plus size={18} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div className="product-price">
                          {trovaPrezzoScontato(v) ? <small>{formatPrice(trovaPrezzo(v))}</small> : formatPrice(trovaPrezzo(v))}
                          {trovaPrezzoScontato(v) && <b>&nbsp;*{formatPrice(trovaPrezzoScontato(v))}</b>}
                        </div>
                      </div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {isMobile && template === "mixed" && (
          <div style={{ padding: "10px", textAlign: "center", backgroundColor: "#fff", borderTop: "1px solid #eee", position: "absolute", bottom: "12vh", left: 0, width: "100%", zIndex: 10 }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowVariants(!showVariants);
              }}
              title="visualizza com è indossata"
              style={{
                padding: "10px",
                backgroundColor: "#2e98f0",
                color: "white",
                border: "none",
                borderRadius: "8px",
                margin: "0 auto",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
              }}
            >
              {showVariants ? "Mostra come è indossata" : "Mostra varianti"}
              &nbsp;
              {showVariants ? <EyeOff size={24} /> : <Eye size={24} />}
            </button>
          </div>
        )}

        <footer className="footer">
          <p className="footer-text">
            {pagina.showDimensions ? measure || variantsMeasure : null}
          </p>
          <p>{ord}</p>
        </footer>

        <div className="debug-controls" style={{ display: "none" }}>
          <span>Controlli Simulazione: </span>
          <button
            onClick={removeVariant}
            className="btn"
            disabled={variants.length === 0}
          >
            - Rimuovi Variante
          </button>
          <span style={{ margin: "0 10px" }}>{variants.length} Varianti</span>
          <button onClick={addVariant} className="btn">
            + Aggiungi Variante
          </button>
        </div>
      </div>
    </>
  );
};

ProductSplitView.propTypes = {
  product: PropTypes.object,
  addToCart: PropTypes.func,
  addToWishlist: PropTypes.func,
  cartItems: PropTypes.array,
};

const mapDispatchToProps = (dispatch) => {
  return {
    addToCart: (
      item,
      addToast,
      quantityCount,
      selectedProductColor,
      selectedProductSize,
      selectedIDArt,
      Ord1,
      Ord2
    ) => {
      dispatch(
        addToCart(
          item,
          addToast,
          quantityCount,
          selectedProductColor,
          selectedProductSize,
          selectedIDArt,
          Ord1,
          Ord2
        )
      );
    },

    addToWishlist: (item, addToast) => {
      dispatch(addToWishlist(item, addToast));
    },
  };
};

const mapStateToProps = (state) => {
  return {
    cartItems: state.cartData,
    userData: state.userData,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductSplitView);